import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { confirmIfFarDate } from "@/lib/dateGuard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Upload, Trash2, FileInput, Calendar, Scissors, Eye, FileText, Pencil, AlertTriangle, SplitSquareHorizontal } from "lucide-react";
import { toast } from "sonner";
import { confirm } from "@/lib/confirm";
import HospitalPicker from "@/components/HospitalPicker";
import SplitSectionsDialog from "./rawMaps/SplitSectionsDialog";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const [y, m, d] = iso.substring(0, 10).split("-");
    return `${d}/${m}/${y}`;
  } catch { return iso; }
}

export default function RawMapsPage() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [manualOpen, setManualOpen] = useState(false);
  const [upOpen, setUpOpen] = useState(false);
  const [manualForm, setManualForm] = useState({ source_date: "", hospital_id: null, hospital_name: "", center: "Centro Cirúrgico II", raw_text: "" });
  const [upForm, setUpForm] = useState({ source_date: "", hospital_id: null, hospital_name: "", center: "", file: null });
  const [loading, setLoading] = useState(false);
  const [split, setSplit] = useState({ open: false, rawMapId: null });

  async function load() {
    try {
      const r = await api.get("/raw-maps");
      setItems(r.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  function parseManualText(text) {
    // Naive: each non-empty line becomes a row under current period
    const rows = [];
    let period = "manha";
    let order = 1;
    for (const line of text.split("\n")) {
      const t = line.trim();
      if (!t) continue;
      const up = t.toUpperCase();
      if (up.includes("MANHÃ") || up.includes("MANHA")) { period = "manha"; continue; }
      if (up.includes("TARDE")) { period = "tarde"; continue; }
      if (up.includes("NOITE")) { period = "noite"; continue; }
      rows.push({ order: order++, period, raw_line: t });
    }
    return rows;
  }

  async function saveManual() {
    if (!(await confirmIfFarDate(manualForm.source_date, "data do mapa"))) return;
    setLoading(true);
    try {
      const rows = parseManualText(manualForm.raw_text || "");
      await api.post("/raw-maps", {
        source_date: manualForm.source_date,
        hospital_id: manualForm.hospital_id || null,
        hospital_name: manualForm.hospital_name || null,
        center: manualForm.center,
        raw_text: manualForm.raw_text,
        rows,
      });
      toast.success("Mapa bruto criado");
      setManualOpen(false);
      setManualForm({ source_date: "", hospital_id: null, hospital_name: "", center: "", raw_text: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }

  async function saveUpload() {
    if (!upForm.file || !upForm.source_date) { toast.error("Arquivo e data são obrigatórios"); return; }
    if (!(await confirmIfFarDate(upForm.source_date, "data do mapa"))) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", upForm.file);
      fd.append("source_date", upForm.source_date);
      if (upForm.hospital_id) fd.append("hospital_id", upForm.hospital_id);
      if (upForm.hospital_name) fd.append("hospital_name", upForm.hospital_name);
      if (upForm.center) fd.append("center", upForm.center);
      const r = await api.post("/raw-maps/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const splits = r.data?.split_maps || [];
      const unresolved = r.data?.unresolved_locations || {};
      toast.success("PDF importado. Revise as linhas se necessário.");
      if (splits.length) {
        toast.info(`Seção de outro local separada em mapa próprio: ${splits.map((s) => `${s.hospital_name} (${s.rows} linhas)`).join("; ")}`, { duration: 9000 });
      }
      const unres = Object.entries(unresolved);
      if (unres.length) {
        toast.warning(`Seção "${unres.map(([l, n]) => `${l}: ${n} linha(s)`).join(", ")}" ficou no mapa principal — cadastre esse local em Hospitais (nome ou sigla igual à seção) e use "Separar seções".`, { duration: 9000 });
      }
      setUpOpen(false);
      setUpForm({ source_date: "", hospital_id: null, hospital_name: "", center: "", file: null });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }

  async function filterNow(item) {
    try {
      const r = await api.post(`/raw-maps/${item.id}/filter`);
      if (r.data.already_existed) {
        toast.info("Este mapa bruto já tinha um mapa filtrado — abrindo o existente (nada foi duplicado).");
        nav(`/mapa-filtrado/${r.data.id}`);
        return;
      }
      const s = r.data.stats || {};
      const matched = (r.data.rows || []).length;
      if (matched === 0) {
        toast.error(`Nenhuma cirurgia da sua equipe foi identificada em ${s.raw_rows_total || "?"} linhas. Confira se os cirurgiões estão cadastrados em "Cirurgiões" com "Equipe = Sim".`);
      } else {
        toast.success(`${matched} de ${s.raw_rows_total || matched} cirurgias identificadas como da sua equipe.`);
      }
      nav(`/mapa-filtrado/${r.data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function remove(item) {
    if (!(await confirm({ description: "Excluir este mapa bruto?" }))) return;
    try {
      await api.delete(`/raw-maps/${item.id}`);
      toast.success("Excluído");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  // Edit metadata (hospital, center, date)
  const [editing, setEditing] = useState(null); // { id, source_date, hospital_name, center }
  const [editLoading, setEditLoading] = useState(false);

  async function saveEdit() {
    if (!editing) return;
    if (!(await confirmIfFarDate(editing.source_date, "data do mapa"))) return;
    setEditLoading(true);
    try {
      await api.patch(`/raw-maps/${editing.id}`, {
        source_date: editing.source_date || null,
        hospital_id: editing.hospital_id || null,
        hospital_name: editing.hospital_name || null,
        center: editing.center || null,
      });
      toast.success("Mapa atualizado. Mapas filtrados e casos vinculados também foram corrigidos.");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setEditLoading(false); }
  }

  const [viewer, setViewer] = useState(null);
  const [viewerBlobUrl, setViewerBlobUrl] = useState(null);

  async function openRaw(m) {
    setViewer(m);
    setViewerBlobUrl(null);
    if (m.source_type === "pdf" && m.file_url) {
      try {
        const r = await api.get(m.file_url.replace(/^\/api/, ""), { responseType: "blob" });
        setViewerBlobUrl(URL.createObjectURL(r.data));
      } catch {
        toast.warning("PDF não disponível (mapa antigo sem arquivo salvo). Mostrando linhas parseadas.");
      }
    }
  }

  useEffect(() => {
    return () => { if (viewerBlobUrl) URL.revokeObjectURL(viewerBlobUrl); };
  }, [viewerBlobUrl]);

  return (
    <div className="space-y-5" data-testid="raw-maps-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Mapa Bruto</h1>
          <p className="text-sm text-slate-500 mt-1">
            Importe o mapa cirúrgico completo do hospital. Será filtrado automaticamente por cirurgião da sua equipe.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setSplit({ open: true, rawMapId: null })} data-testid="btn-split-sections"
            title="Mapas já importados com seção de outro local (ex.: CDE dentro do PDF do HGR)">
            <SplitSquareHorizontal className="h-4 w-4 mr-2" /> Separar seções (CDE)
          </Button>
          <Dialog open={upOpen} onOpenChange={setUpOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="btn-upload-pdf">
                <Upload className="h-4 w-4 mr-2" /> Upload PDF
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Importar mapa bruto (PDF)</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-1.5">
                  <Label>Data do mapa *</Label>
                  <Input type="date" value={upForm.source_date}
                    onChange={(e) => setUpForm({ ...upForm, source_date: e.target.value })}
                    data-testid="upload-date" />
                </div>
                <div className="space-y-1.5">
                  <Label>Hospital</Label>
                  <HospitalPicker value={upForm} testid="upload-hospital"
                    onChange={(h) => setUpForm({ ...upForm, ...h })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Centro Cirúrgico</Label>
                  <Input value={upForm.center}
                    onChange={(e) => setUpForm({ ...upForm, center: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Arquivo PDF *</Label>
                  <Input type="file" accept="application/pdf"
                    onChange={(e) => setUpForm({ ...upForm, file: e.target.files?.[0] || null })}
                    data-testid="upload-file" />
                  <p className="text-xs text-slate-500">
                    A extração é heurística. Após importar, revise as linhas e use “Gerar mapa filtrado”.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setUpOpen(false)}>Cancelar</Button>
                <Button onClick={saveUpload} disabled={loading} className="bg-blue-700 hover:bg-blue-800" data-testid="upload-submit">
                  {loading ? "Enviando..." : "Importar"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={manualOpen} onOpenChange={setManualOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-700 hover:bg-blue-800" data-testid="btn-manual">
                <Plus className="h-4 w-4 mr-2" /> Entrada manual
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden" data-testid="manual-raw-map-dialog">
              <DialogHeader><DialogTitle>Entrada manual do mapa bruto</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2 overflow-y-auto flex-1 min-h-0 pr-1">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label>Data *</Label>
                    <Input type="date" value={manualForm.source_date}
                      onChange={(e) => setManualForm({ ...manualForm, source_date: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Hospital</Label>
                    <HospitalPicker value={manualForm} testid="manual-hospital"
                      onChange={(h) => setManualForm({ ...manualForm, ...h })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Centro</Label>
                    <Input value={manualForm.center}
                      onChange={(e) => setManualForm({ ...manualForm, center: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Colar texto do mapa</Label>
                  <Textarea rows={10} placeholder="Cole o texto do mapa bruto. Use linhas com MANHÃ / TARDE / NOITE para separar períodos."
                    value={manualForm.raw_text}
                    onChange={(e) => setManualForm({ ...manualForm, raw_text: e.target.value })} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setManualOpen(false)}>Cancelar</Button>
                <Button onClick={saveManual} disabled={loading} className="bg-blue-700 hover:bg-blue-800">
                  {loading ? "Salvando..." : "Salvar"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-4 py-3">Data</th>
                <th className="text-left px-4 py-3">Hospital / Centro</th>
                <th className="text-left px-4 py-3">Origem</th>
                <th className="text-left px-4 py-3">Linhas</th>
                <th className="w-80 px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={5} className="text-center p-8 text-slate-400">Nenhum mapa importado.</td></tr>}
              {items.map((m, idx) => (
                <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`raw-map-row-${idx}`}>
                  <td className="px-4 py-3"><Calendar className="h-3.5 w-3.5 inline mr-1 text-slate-400" /> {fmtDate(m.source_date)}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{m.hospital_name || "—"}</div>
                    <div className="text-xs text-slate-500">{m.center || ""}</div>
                    {m.split_from_location && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-sky-700 bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5"
                        data-testid={`raw-map-split-${idx}`}
                        title="Seção deste local veio dentro do PDF de outro hospital e foi separada automaticamente.">
                        <Scissors className="h-3 w-3" /> separado do PDF ({m.split_from_location})
                      </div>
                    )}
                    {m.unresolved_locations && Object.keys(m.unresolved_locations).some((l) => !/^cc\b/i.test(l)) && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"
                        data-testid={`raw-map-unresolved-loc-${idx}`}
                        title="O PDF tem seção de outro local que não está cadastrado como hospital. Cadastre-o e reimporte para separar.">
                        <AlertTriangle className="h-3 w-3" /> contém seção {Object.keys(m.unresolved_locations).filter((l) => !/^cc\b/i.test(l)).join(", ")}
                      </div>
                    )}
                    {m.hospital_name && !m.hospital_id && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"
                        data-testid={`raw-map-unlinked-hospital-${idx}`}
                        title="Este nome não corresponde a nenhum hospital cadastrado. Cadastre-o em Hospitais (ou edite o mapa) para a produção agrupar por hospital.">
                        <AlertTriangle className="h-3 w-3" /> hospital não cadastrado
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600 uppercase text-xs">{m.source_type}</td>
                  <td className="px-4 py-3">{(m.rows || []).length}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 justify-end">
                      <Button size="sm" variant="outline" onClick={() => openRaw(m)} data-testid={`raw-map-open-${idx}`}>
                        <Eye className="h-3.5 w-3.5 mr-1" /> {m.source_type === "pdf" ? "Abrir PDF" : "Ver linhas"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditing({ id: m.id, source_date: m.source_date || "", hospital_id: m.hospital_id || null, hospital_name: m.hospital_name || "", center: m.center || "" })}
                        data-testid={`raw-map-edit-${idx}`}
                        title="Editar hospital, centro ou data"
                      >
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Editar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => filterNow(m)} data-testid={`raw-map-filter-${idx}`}>
                        <Scissors className="h-3.5 w-3.5 mr-1" /> Gerar mapa filtrado
                      </Button>
                      {m.source_type === "pdf" && !m.parent_raw_map_id && (
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-sky-700 hover:bg-sky-50"
                          onClick={() => setSplit({ open: true, rawMapId: m.id })}
                          data-testid={`raw-map-split-sections-${idx}`}
                          title="Separar seção de outro local (ex.: CDE) deste PDF">
                          <SplitSquareHorizontal className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => remove(m)} data-testid={`raw-map-delete-${idx}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={!!viewer} onOpenChange={(v) => !v && setViewer(null)}>
        <DialogContent className="max-w-5xl h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Mapa bruto — {viewer && fmtDate(viewer.source_date)}
            </DialogTitle>
          </DialogHeader>
          {viewer?.source_type === "pdf" && viewerBlobUrl ? (
            <iframe src={viewerBlobUrl} title="Mapa bruto" className="flex-1 w-full border border-slate-200 rounded" />
          ) : viewer ? (
            <div className="flex-1 overflow-auto border border-slate-200 rounded p-3 text-xs font-mono whitespace-pre-wrap">
              {(viewer.rows || []).length === 0 && <div className="text-slate-400">Sem linhas parseadas.</div>}
              {(viewer.rows || []).map((r) => (
                <div key={`${r.order}-${r.period}`} className="py-1 border-b border-slate-100">
                  <span className="text-slate-400">{(r.period || "").padEnd(6)}</span>{" "}
                  <span className="text-slate-600">{r.time || "--:--"}</span>{" · "}
                  <span className="font-semibold">{r.patient_name}</span>
                  {r.procedure_name && <> — {r.procedure_name}</>}
                  {r.surgeon_name && <span className="text-slate-500"> · Dr(a) {r.surgeon_name}</span>}
                </div>
              ))}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="raw-map-edit-dialog">
          <DialogHeader>
            <DialogTitle>Editar mapa bruto</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 py-2">
              <p className="text-xs text-slate-500">
                As alterações são propagadas automaticamente para o mapa filtrado e casos cirúrgicos vinculados.
              </p>
              <div className="space-y-1.5">
                <Label>Data do mapa</Label>
                <Input
                  type="date"
                  value={editing.source_date || ""}
                  onChange={(e) => setEditing({ ...editing, source_date: e.target.value })}
                  data-testid="edit-raw-date"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Hospital</Label>
                <HospitalPicker value={editing} testid="edit-raw-hospital"
                  onChange={(h) => setEditing({ ...editing, ...h })} />
              </div>
              <div className="space-y-1.5">
                <Label>Centro Cirúrgico</Label>
                <Input
                  value={editing.center || ""}
                  onChange={(e) => setEditing({ ...editing, center: e.target.value })}
                  placeholder="Centro Cirúrgico II"
                  data-testid="edit-raw-center"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={saveEdit} disabled={editLoading} className="bg-blue-700 hover:bg-blue-800" data-testid="btn-save-edit-raw">
              {editLoading ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <SplitSectionsDialog open={split.open} rawMapId={split.rawMapId}
        onOpenChange={(o) => setSplit((s) => ({ ...s, open: o }))} onDone={load} />
    </div>
  );
}
